import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { memberAdminAPI, contactAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { ROLE_INFO, ASSIGNABLE_ROLES } from '../../utils/roles';

// ── Role Badge (admin version) ────────────────────────────────
function AdminRoleBadge({ role }) {
  const info = ROLE_INFO[role];
  if (!info) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${info.color}`}>
      {info.icon} {info.label}
    </span>
  );
}

// ── Role Assign Modal ─────────────────────────────────────────
function AssignRoleModal({ member, onClose, onSaved }) {
  const [role, setRole]   = useState(member.member_role === 'pending' ? 'the_lovers' : member.member_role);
  const [note, setNote]   = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (role === member.member_role) { onClose(); return; }
    setSaving(true);
    try {
      const res = await memberAdminAPI.assignRole(member.id, { role, note });
      toast.success(res.data.message);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign role.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
        <h3 className="font-bold text-navy-800 text-lg mb-1">Assign Membership Role</h3>
        <p className="text-gray-500 text-sm mb-5">
          Assigning role for <strong>{member.full_name}</strong>. They will be notified by email.
        </p>

        {/* Role options */}
        <div className="space-y-2.5 mb-5">
          {ASSIGNABLE_ROLES.map(r => (
            <button
              key={r.value}
              onClick={() => setRole(r.value)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${role === r.value ? 'border-navy-700 bg-navy-50' : 'border-gray-100 hover:border-gray-200'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-lg ${r.color} flex-shrink-0`}>
                  {r.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-navy-800 text-sm">{r.label}</p>
                    <p className="arabic text-navy-500 text-sm">{r.label_ar}</p>
                  </div>
                  <p className="text-gray-400 text-xs mt-0.5">{r.description}</p>
                </div>
                <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${role === r.value ? 'border-navy-700 bg-navy-700' : 'border-gray-200'}`} />
              </div>
            </button>
          ))}
        </div>

        {/* Internal note */}
        <div className="mb-5">
          <label className="form-label">Internal Note <span className="text-gray-400 font-normal">(not shown to member)</span></label>
          <textarea
            rows={2}
            className="form-input resize-none text-sm"
            placeholder="e.g. Confirmed initiated by Alhaji Musa, Lagos Sawia"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2">
            {saving ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Saving...</> : 'Assign Role & Notify'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN MEMBERS PAGE
// ══════════════════════════════════════════════════════════════
export function AdminMembersPage() {
  const [members, setMembers]   = useState([]);
  const [stats, setStats]       = useState({});
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [loading, setLoading]   = useState(true);

  const fetchAll = () => {
    setLoading(true);
    memberAdminAPI.getAll({ role: roleFilter, search, limit: 200 }).then(r => {
      setMembers(r.data.data || []);
      setStats(r.data.stats || {});
    }).finally(() => setLoading(false));
  };
  useEffect(fetchAll, [roleFilter]);

  const del = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the system? This cannot be undone.`)) return;
    await memberAdminAPI.delete(id);
    toast.success('Member removed.');
    setSelected(null);
    fetchAll();
  };

  const ROLE_FILTERS = [
    { value: '',               label: `All (${stats.total || 0})` },
    { value: 'pending',        label: `Pending (${stats.pending || 0})` },
    { value: 'the_lovers',     label: `The Lovers (${stats.the_lovers || 0})` },
    { value: 'general_member', label: `General (${stats.general_member || 0})` },
    { value: 'fully_member',   label: `Fully (${stats.fully_member || 0})` },
    { value: 'muqaddam',       label: `Muqaddam (${stats.muqaddam || 0})` },
  ];

  return (
    <AdminLayout>
      {assignModal && (
        <AssignRoleModal
          member={assignModal}
          onClose={() => setAssignModal(null)}
          onSaved={() => { fetchAll(); setSelected(prev => prev?.id === assignModal.id ? { ...prev, member_role: assignModal.member_role } : prev); }}
        />
      )}

      <div className="mb-5">
        <h1 className="text-xl font-bold text-navy-800">Member Management</h1>
        <p className="text-gray-400 text-sm mt-0.5">Assign membership roles — everyone is welcome in JNF</p>
      </div>

      {/* Role stat pills */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {ROLE_FILTERS.map(f => (
          <button key={f.value} onClick={() => setRoleFilter(f.value)}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all border ${roleFilter === f.value ? 'bg-navy-700 text-white border-navy-700' : 'bg-white border-gray-200 text-gray-600 hover:border-navy-300'}`}>
            {f.label}
          </button>
        ))}
        <div className="flex gap-2 ml-auto">
          <input className="form-input py-2 text-sm w-48" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter'&&fetchAll()}/>
          <button onClick={fetchAll} className="btn-primary py-2 px-4 text-sm">Search</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Member list */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? <div className="p-8 text-center text-gray-400">Loading...</div>
          : members.length === 0 ? <div className="p-12 text-center text-gray-400">No members found.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {['Member','Role','Location','Joined',''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {members.map(m => (
                    <tr key={m.id} onClick={() => setSelected(m)}
                      className={`cursor-pointer transition-colors hover:bg-gray-50 ${selected?.id===m.id?'bg-cyan-50':''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-full bg-navy-50 overflow-hidden flex-shrink-0">
                            {m.profile_photo ? <img src={m.profile_photo} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-sm text-navy-300">👤</div>}
                          </div>
                          <div>
                            <p className="font-medium text-navy-800 text-sm">{m.full_name}</p>
                            <p className="text-xs text-gray-400">{m.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><AdminRoleBadge role={m.member_role}/></td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{m.home_town || '—'}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <button onClick={e=>{e.stopPropagation();setSelected(m);setAssignModal(m);}}
                          className="text-xs font-semibold text-cyan-600 hover:text-cyan-700 bg-cyan-50 hover:bg-cyan-100 px-2.5 py-1 rounded-lg transition-all whitespace-nowrap">
                          Assign Role
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-14 w-14 rounded-full bg-navy-50 overflow-hidden flex-shrink-0">
                  {selected.profile_photo ? <img src={selected.profile_photo} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-2xl">👤</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-navy-800">{selected.full_name}</p>
                  <AdminRoleBadge role={selected.member_role}/>
                </div>
              </div>

              <div className="space-y-2 mb-4 text-sm">
                {[
                  ['Email',     selected.email],
                  ['Phone',     selected.phone],
                  ['Home Town', selected.home_town],
                  ['Country',   selected.country],
                  ['Sawia',     selected.sawia_name],
                  ['Joined',    new Date(selected.created_at).toLocaleDateString()],
                  ['Role set',  selected.role_assigned_at ? new Date(selected.role_assigned_at).toLocaleDateString() : null],
                  ['Assigned by', selected.assigned_by_name],
                  ['Last login', selected.last_login ? new Date(selected.last_login).toLocaleString() : 'Never'],
                ].filter(([,v]) => v).map(([l,v]) => (
                  <div key={l} className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-gray-400 font-medium">{l}</span>
                    <span className="text-navy-700 text-right max-w-[160px] truncate capitalize">{v}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <button onClick={() => setAssignModal(selected)} className="btn-primary flex-1 text-sm py-2.5">
                  🌟 Assign Role
                </button>
                <button onClick={() => del(selected.id, selected.full_name)} className="p-2.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-xl transition-all border border-gray-100">
                  <TrashIcon className="h-4 w-4"/>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 h-52 flex items-center justify-center text-gray-400 text-sm">
              Select a member to view details
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN MESSAGES INBOX (unchanged from v2 — included for completeness)
// ══════════════════════════════════════════════════════════════
export function AdminMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread]     = useState(0);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatus] = useState('');
  const [loading, setLoading]   = useState(true);
  const [replyNote, setReplyNote] = useState('');

  const fetch = () => {
    setLoading(true);
    contactAPI.getMessages({ status: statusFilter, limit: 100 }).then(r => {
      setMessages(r.data.data || []);
      setUnread(r.data.unread_count || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(fetch, [statusFilter]);

  const open = async (msg) => {
    setSelected(msg);
    if (msg.status === 'unread') { await contactAPI.markRead(msg.id); fetch(); }
  };

  const markReplied = async () => {
    await contactAPI.markReplied(selected.id, { reply_note: replyNote });
    toast.success('Marked as replied.'); setReplyNote(''); fetch(); setSelected(null);
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    await contactAPI.delete(id); toast.success('Deleted.'); setSelected(null); fetch();
  };

  const sColor = { unread:'bg-blue-100 text-blue-700', read:'bg-gray-100 text-gray-600', replied:'bg-green-100 text-green-700' };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-navy-800">Contact Messages</h1>
          {unread > 0 && <p className="text-blue-600 text-sm font-medium">{unread} unread</p>}
        </div>
        <div className="flex gap-2">
          {['','unread','read','replied'].map(s => (
            <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all border ${statusFilter===s?'bg-navy-700 text-white border-navy-700':'border-gray-200 text-gray-600'}`}>
              {s||'All'}
            </button>
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? <div className="p-8 text-center text-gray-400">Loading...</div>
          : messages.length === 0 ? <div className="p-10 text-center text-gray-400">No messages.</div>
          : messages.map(msg => (
            <div key={msg.id} onClick={() => open(msg)}
              className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${selected?.id===msg.id?'bg-cyan-50 border-l-4 border-l-cyan-400':''}  ${msg.status==='unread'?'bg-blue-50/40':''}`}>
              <div className="flex justify-between mb-0.5">
                <p className={`text-sm font-semibold text-navy-800 ${msg.status==='unread'?'font-bold':''}`}>{msg.sender_name}</p>
                <span className={`badge text-xs ${sColor[msg.status]}`}>{msg.status}</span>
              </div>
              <p className="text-xs text-gray-500 mb-1">{msg.subject}</p>
              {msg.sawia_name && <p className="text-xs text-cyan-600">📍 {msg.sawia_name}</p>}
              <p className="text-xs text-gray-300 mt-1">{new Date(msg.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
        <div className="lg:col-span-3">
          {selected ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex justify-between mb-4">
                <div>
                  <h3 className="font-bold text-navy-800 text-lg">{selected.subject}</h3>
                  <p className="text-sm text-gray-500">From: <strong>{selected.sender_name}</strong> · <a href={`mailto:${selected.sender_email}`} className="text-cyan-600">{selected.sender_email}</a></p>
                  {selected.sender_phone && <p className="text-sm text-gray-500">📞 {selected.sender_phone}</p>}
                  {selected.sawia_name && <p className="text-sm text-cyan-600 font-medium">📍 {selected.sawia_name}</p>}
                </div>
                <button onClick={() => del(selected.id)} className="p-2 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg"><TrashIcon className="h-4 w-4"/></button>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
              <p className="text-gray-400 text-xs mb-4">{new Date(selected.created_at).toLocaleString()}</p>
              {selected.status !== 'replied' ? (
                <div>
                  <label className="form-label">Reply Note (internal)</label>
                  <textarea rows={2} className="form-input resize-none mb-3 text-sm" value={replyNote} onChange={e=>setReplyNote(e.target.value)} placeholder="Note about your reply..."/>
                  <div className="flex gap-2">
                    <a href={`mailto:${selected.sender_email}?subject=Re: ${encodeURIComponent(selected.subject)}`} className="btn-primary text-sm py-2.5 px-5 flex-1 text-center">📧 Reply via Email</a>
                    <button onClick={markReplied} className="btn-outline text-sm py-2.5 px-5">Mark Replied</button>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 rounded-xl p-3 text-green-700 text-sm">✅ Replied {new Date(selected.replied_at).toLocaleDateString()}</div>
              )}
            </div>
          ) : <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 h-64 flex items-center justify-center text-gray-400 text-sm">Select a message</div>}
        </div>
      </div>
    </AdminLayout>
  );
}
